import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, Box, Typography } from '@mui/material';
import { generateGuid } from 'helpers';
import { useTranslation } from 'react-i18next';
import { TooltipCustom } from 'components/common';

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function FormInputText(props) {
  const {
    position = 'vertical',
    name,
    control,
    label,
    rules,
    InputProps,
    type = 'text',
    inputProps,
    placeholder = 'common.rules.placeholder',
    disabled = false,
    size = 'small',
    variant = 'outlined',
    defaultValue = '',
    onKeyDown = null,
    sx,
    onClick,
    onFocus,
    onBlur,
    className,
    helperText,
    multiline,
    minRows,
    maxRows
  } = props;
  const onChangeInput = props.onChange;
  const [t, i18n] = useTranslation();
  const defaultId = !props?.id && `textfield-${generateGuid()}`;
  const [showPassword, setShowPassword] = useState(false);
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        flexDirection: position === 'vertical' ? 'column' : 'row',
        alignItems: position === 'vertical' ? 'flex-start' : 'center',
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        <Typography variant="body1" id={`label-title-${defaultId}`}>
          {i18n.exists(label) ? t(label) : label}
        </Typography>
        {rules?.required?.value && rules.required.value && (
          <Typography color="red" variant="body1">
            *
          </Typography>
        )}
        {helperText && <TooltipCustom title={helperText} />}
      </Box>
      <Controller
        id={defaultId}
        name={name ?? defaultId}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: rules?.required && {
            value: rules.required.value,
            message: t(
              rules.required?.message
                ? rules.required.message
                : 'common.rules.required'
            ),
          },
          pattern: rules?.pattern && {
            value: rules.pattern.value,
            message: t(
              rules.pattern?.message ? rules.pattern.message : 'common.rules.pattern'
            ),
          },
          maxLength: rules?.maxLength && {
            value: rules.maxLength.value,
            message: t(
              rules.maxLength?.message
                ? rules.maxLength.message
                : 'common.rules.maxLength',
              { maxLength: rules.maxLength.value }
            ),
          },
          minLength: rules?.minLength && {
            value: rules.minLength.value,
            message: t(
              rules.minLength?.message
                ? rules.minLength.message
                : 'common.rules.minLength',
              { minLength: rules.minLength.value }
            ),
          },
          min: rules?.min && {
            value: rules.min,
            message: t(rules.min?.message ? rules.min.message : 'common.rules.min', {
              min: rules.min.value,
            }),
          },
          max: rules?.max && {
            value: rules.max,
            message: t(rules.max?.message ? rules.max.message : 'common.rules.max', {
              max: rules.max.value,
            }),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            helperText={error ? t(error.message) : null}
            size={size ? size : 'medium'}
            error={!!error}
            onChange={(event)=>{
              onChange(event)
              onChangeInput && onChangeInput(event)
              return true
            }}
            value={value}
            // label={i18n.exists(label) ? t(label) : label}
            variant={variant ? variant : 'outlined'}
            placeholder={
              placeholder
                ? i18n.exists(placeholder)
                  ? t(placeholder)
                  : placeholder
                : null
            }
            disabled={disabled}
            inputProps={inputProps ?? {}}
            InputProps={
              type === 'password' ? 
              {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              } : (InputProps ?? {})
            }
            type={type==='password' && showPassword ? 'text' : type}
            onKeyDown={onKeyDown}
            sx={sx}
            onClick={onClick}
            onFocus={onFocus}
            onBlur={onBlur}
            className={className}
            multiline={multiline}
            minRows={minRows}
            maxRows={maxRows}
            min={10}
            fullWidth
          />
        )}
      />
    </Box>
  );
}
